import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { motion, useReducedMotion } from 'framer-motion'
import { useState } from 'react'
import MemoryDocumentsMode from './MemoryDocumentsMode'
import MemoryRecallMode from './MemoryRecallMode'
import type { MemoryCopy, MemoryDocumentSummary, MemoryGraphPayload, MemoryInspectorMode, MemoryRow } from './types'

interface Props {
  workerId: string
  copy: MemoryCopy
  canManage: boolean
  refreshKey: number
  filterResetKey: number
  onSelectMemory: (row: MemoryRow) => void
  onSelectDocument: (document: MemoryDocumentSummary) => void
  onPreviewMemory: (row: MemoryRow) => void
  onPreviewDocument: (document: MemoryDocumentSummary, focusedGraph?: MemoryGraphPayload, isolate?: boolean) => void
  onDocumentSearchResults: (documents: MemoryDocumentSummary[], query: string, graph?: MemoryGraphPayload) => void
  onRecallSearch: (query: string) => void
  onClearRecall: () => void
  onAddDocument: () => void
}

export default function MemoryInspectorSidebar({
  workerId, copy, canManage, refreshKey, filterResetKey, onSelectMemory, onSelectDocument, onPreviewMemory, onPreviewDocument, onDocumentSearchResults, onRecallSearch, onClearRecall, onAddDocument,
}: Props) {
  const [mode, setMode] = useState<MemoryInspectorMode>('recall')
  const [documentsMounted, setDocumentsMounted] = useState(false)
  const reduceMotion = useReducedMotion()
  const panelTransition = reduceMotion ? { duration: 0 } : { duration: 0.24, ease: [0.22, 1, 0.36, 1] as const }

  const changeMode = (nextMode: string) => {
    const next = nextMode as MemoryInspectorMode
    if (next === 'documents') setDocumentsMounted(true)
    setMode(next)
  }

  return (
    <aside className="h-[620px] min-h-0 overflow-hidden border-t border-border bg-card lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:h-full lg:border-l lg:border-t-0" aria-label={copy.inspector.label}>
      <Tabs value={mode} onValueChange={changeMode} className="flex h-full min-h-0 flex-col gap-0">
        <header data-testid="memory-inspector-header" className="flex h-16 shrink-0 items-center border-b border-border px-3">
          <motion.div layout className="min-w-0 flex-1" transition={panelTransition}>
            <TabsList className="grid h-9 w-full grid-cols-2 rounded-lg">
              <TabsTrigger value="recall">{copy.inspector.recall}</TabsTrigger>
              <TabsTrigger value="documents">{copy.inspector.documents}</TabsTrigger>
            </TabsList>
          </motion.div>
        </header>

        <div className="relative min-h-0 flex-1 overflow-hidden">
          <motion.div
            aria-hidden={mode !== 'recall'}
            inert={mode !== 'recall'}
            className={`absolute inset-0 ${mode === 'recall' ? '' : 'pointer-events-none'}`}
            initial={false}
            animate={{ opacity: mode === 'recall' ? 1 : 0, x: mode === 'recall' ? 0 : -12, scale: mode === 'recall' ? 1 : 0.995 }}
            transition={panelTransition}
          >
            <MemoryRecallMode workerId={workerId} copy={copy} refreshKey={refreshKey} resetKey={filterResetKey} onSelect={onSelectMemory} onPreview={onPreviewMemory} onSearch={onRecallSearch} onClearSearch={onClearRecall} />
          </motion.div>
          {documentsMounted && (
            <motion.div
              aria-hidden={mode !== 'documents'}
              inert={mode !== 'documents'}
              className={`absolute inset-0 ${mode === 'documents' ? '' : 'pointer-events-none'}`}
              initial={reduceMotion ? false : { opacity: 0, x: 12, scale: 0.995 }}
              animate={{ opacity: mode === 'documents' ? 1 : 0, x: mode === 'documents' ? 0 : 12, scale: mode === 'documents' ? 1 : 0.995 }}
              transition={panelTransition}
            >
              <MemoryDocumentsMode workerId={workerId} copy={copy} canManage={canManage} refreshKey={refreshKey} resetKey={filterResetKey} onSelect={onSelectDocument} onPreview={onPreviewDocument} onSearchResults={onDocumentSearchResults} onClearFocus={onClearRecall} onAddDocument={onAddDocument} />
            </motion.div>
          )}
        </div>
      </Tabs>
    </aside>
  )
}
